export function initMainWindowUploader(options) {
  if (window.__uploaderBridgeInitialized) return;
  window.__uploaderBridgeInitialized = true;

  const CHUNK_SIZE = (options && options.chunkSizeBytes) || 8 * 1024 * 1024;
  const UPLOAD_URL = (options && options.uploadUrl) || '/api/wt/documents/uploadfile';
  const FILE_HOST  = (options && options.fileHost)  || 'https://filehost.wtd';

  function postToHost(msg) {
    if (window.chrome && window.chrome.webview) {
      window.chrome.webview.postMessage(msg);
    } else {
      console.error("[main] WebView2 host bridge not available");
    }
  }

  function fetchRangeBlob(fileUrl, start, endExclusive) {
    const endInclusive = endExclusive - 1;
    return fetch(fileUrl, {
      headers: { 'Range': 'bytes=' + start + '-' + endInclusive },
      cache: 'no-store',
      credentials: 'include'
    }).then(res => {
      if (!(res.status === 206 || res.status === 200)) {
        throw new Error('Range fetch failed: ' + res.status);
      }
      return res.blob();
    });
  }

  function postChunk(formData) {
    return fetch(UPLOAD_URL, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    }).then(res => {
      if (!res.ok) throw new Error('Upload failed: ' + res.status);
      return null;
    });
  }

  function uploadOne(desc, onProgress) {
    const size = desc.fileSize;
    const totalChunks = Math.ceil(size / CHUNK_SIZE);
    const name = desc.fileName || 'file';
    const relPath = desc.relativePath || name;
    const batchId = desc.batchId || '';
    const objectId = desc.objectId || '';

    let p = Promise.resolve();
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      ((chunkIndex) => {
        p = p.then(() => {
          const start = chunkIndex * CHUNK_SIZE;
          const endExclusive = Math.min(start + CHUNK_SIZE, size);
          return fetchRangeBlob(desc.fileUrl, start, endExclusive).then(chunkBlob => {
            const fd = new FormData();
            fd.append('fileChunk', chunkBlob);
            fd.append('chunkIndex', String(chunkIndex));
            fd.append('totalChunks', String(totalChunks));
            fd.append('batchId', batchId);
            fd.append('fileName', name);
            fd.append('relativePath', relPath);
            fd.append('objectId', objectId);

            return postChunk(fd).then(() => {
              if (onProgress) {
                const pct = Math.round(((chunkIndex + 1) / totalChunks) * 100);
                onProgress(pct);
              }
            });
          });
        });
      })(chunkIndex);
    }
    return p;
  }

  function hostDeleteLocal(token, shouldDelete) {
    postToHost({ channel: 'host', type: 'host-delete-local', token, deleteFile: shouldDelete ? 'true' : 'false' });
  }

  function requestHostBatch(items) {
    return new Promise((resolve, reject) => {
      function onMsg(e) {
        const m = e.data;
        if (!m || m.channel !== 'host') return;
        if (m.type === 'upload-start-batch' && Array.isArray(m.items)) {
          window.chrome.webview.removeEventListener('message', onMsg);
          resolve(m.items);
        } else if (m.type === 'upload-error') {
          window.chrome.webview.removeEventListener('message', onMsg);
          reject(new Error(m.message || 'host error'));
        }
      }
      if (!(window.chrome && window.chrome.webview)) {
        reject(new Error('WebView2 host bridge not available'));
        return;
      }
      window.chrome.webview.addEventListener('message', onMsg);
      postToHost({ channel: 'host', type: 'request-upload-batch', items });
    });
  }

  // PUBLIC: main worker (no immediate delete)
  window.startBatchUploadWithLocalPaths = function (fileDataArray, operationId, keepCheckedOut, cb) {
    const items = [];
    for (let i = 0; i < fileDataArray.length; i++) {
      const f = fileDataArray[i];
      if (!f || !f.localPath) continue;
      items.push({
        localPath: f.localPath,
        fileName: f.fileName || null,
        relativePath: f.objectId || f.fileName,
        batchId: operationId || '',
        objectId: f.objectId || ''
      });
    }
    if (!items.length) {
      cb && cb.onError && cb.onError("No valid items to upload");
      return;
    }

    requestHostBatch(items).then((descs) => {
      const queue = descs.slice(0);
      function next() {
        if (!queue.length) return;
        const d = queue[0];

        uploadOne(d, (pct) => {
          cb && cb.onProgress && cb.onProgress(d.fileName, pct);
        })
        .then(() => {
          cb && cb.onComplete && cb.onComplete(d.fileName, d.objectId, d.token);
          queue.shift();
          next();
        })
        .catch(err => {
          cb && cb.onError && cb.onError(String((err && err.message) || err));
          queue.shift();
          next();
        });
      }
      next();
    }).catch(err => {
      cb && cb.onError && cb.onError(String((err && err.message) || err));
    });
  };

  // ===== Relay for popup -> main (start, finalize) =====
  function onPopupMessage(ev) {
    const m = ev.data || {};
    const source = ev.source;
    const origin = ev.origin || "*";

    if (m.kind === 'upload-batch') {
      window.startBatchUploadWithLocalPaths(m.items, m.operationId, m.keepCheckedOut, {
        onProgress(fileName, percent) {
          source && source.postMessage({ kind: 'upload-progress', fileName, percent }, origin);
        },
        onComplete(fileName, objectId, token) {
          // Include token so popup can finalize/delete later
          source && source.postMessage({ kind: 'upload-complete', fileName, objectId, token }, origin);
        },
        onError(message) {
          source && source.postMessage({ kind: 'upload-error', message }, origin);
        }
      });
      return;
    }

    if (m.kind === 'upload-finalize') {
      if (!m.token) {
        console.warn('[main] finalize missing token');
        return;
      }
      hostDeleteLocal(m.token, !!m.deleteFile);
      // (optional) ack:
      source && source.postMessage({ kind: 'upload-finalized', token: m.token }, origin);
      return;
    }
  }

  window.addEventListener('message', onPopupMessage);

  window.__disposeUploaderBridge = function () {
    window.removeEventListener('message', onPopupMessage);
    window.__uploaderBridgeInitialized = false;
    delete window.startBatchUploadWithLocalPaths;
  };
}
